import React, { useEffect, useState } from "react";
import { useTheme } from "../config/ThemeContext";

export default function EditPlayerDialog({ player, onSave, onCancel }) {
  const theme = useTheme();
  const [form, setForm] = useState({ ...player });
  const [errors, setErrors] = useState({});
  const [globalError, setGlobalError] = useState("");

  useEffect(() => {
    const handleEsc = (e) => e.key === "Escape" && onCancel();
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onCancel]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!form.shirtNumber) errs.shirtNumber = "Shirt number is required.";
    if (!form.lastName) errs.lastName = "Last name is required.";
    if (!form.position) errs.position = "Position is required.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    try {
      await onSave(form);
    } catch (err) {
      setGlobalError(err.message || "Failed to save player.");
    }
  };

  const editableFields = [
    { name: "shirtNumber", label: "Shirt Number", type: "number", short: true },
    { name: "lastName", label: "Last Name", type: "text" },
    { name: "firstName", label: "First Name", type: "text" },
    { name: "shirtName", label: "Shirt Name", type: "text" },
    { name: "position", label: "Position", type: "select" },
    { name: "birthdate", label: "Birthdate", type: "date", short: true },
    { name: "weight", label: "Weight (kg)", type: "number", short: true },
    { name: "height", label: "Height (cm)", type: "number", short: true },
    { name: "spikeReach", label: "Spike Reach (cm)", type: "number", short: true },
    { name: "blockReach", label: "Block Reach (cm)", type: "number", short: true },
    //{ name: "federationOfOrigin", label: "Federation", type: "text" },
    //{ name: "fivbNo", label: "FIVB No.", type: "text" },
  ];

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl p-6">
        <h2 className="text-xl font-semibold mb-4">Edit Player</h2>
        {globalError && <div className="text-red-600 mb-2">{globalError}</div>}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {editableFields.map(({ name, label, type, short }) => (
            <div key={name} className={short ? "max-w-[140px]" : ""}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {label}
              </label>
              {type === "select" ? (
                <select
                  value={form[name] || ""}
                  onChange={(e) => handleChange(name, e.target.value)}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring"
                >
                  <option value="">Select {label}</option>
                  {["Libero", "Wing Spiker", "Opposite", "Middle Blocker", "Setter"].map((role) => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              ) : (
                <input
                  type={type}
                  value={form[name] || ""}
                  onChange={(e) => handleChange(name, e.target.value)}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring"
                />
              )}
              {errors[name] && <p className="text-red-500 text-sm mt-1">{errors[name]}</p>}
            </div>
          ))}
          <div className="flex items-center space-x-2 mt-2">
            <input
              type="checkbox"
              checked={form.isCaptain || false}
              onChange={(e) => handleChange("isCaptain", e.target.checked)}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded"
            />
            <label className="text-sm text-gray-700">Captain</label>
          </div>
        </div>
        <div className="flex justify-end space-x-3">
          <button
            onClick={handleSave}
            disabled={!form.shirtNumber || !form.lastName}
            className={`${theme.buttons.primary} ${!form.shirtNumber || !form.lastName ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            Save
          </button>
          <button onClick={onCancel} className={theme.buttons.secondary}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
